import React, { useState, useMemo, useEffect } from "react";
import TreeNode from "./TreeNode";

const TreeView = ({
  data,
  title,
  onChange,
  handleSelectItem,
  onDoubleClick,
  onFocusOut,
  template,
}) => {
  const [expanded, setExpanded] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [newItemName, setNewItemName] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [newItemType, setNewItemType] = useState("menu");
  const [searchOpen, setSearchOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const buildTree = (items = []) => {
    const map = {};
    const roots = [];

    // Normalize + add propkey
    items.forEach((item, index) => {
      map[item?.id] = {
        ...item,
        propkey: index,
        children: [],
      };
    });

    // Build tree
    items.forEach((item) => {
      const node = map[item?.id];

      if (item?.parentId === 0) {
        roots.push(node);
      } else if (map[item?.parentId]) {
        map[item.parentId].children.push(node);
      }
    });

    // Sort recursively
    const sortTree = (nodes) => {
      nodes.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      nodes.forEach((n) => sortTree(n.children));
    };

    sortTree(roots);
    return roots;
  };

  // const tree = buildTree(data);
  const tree = useMemo(() => buildTree(data), [data]);

  const filterTree = (nodes, term) => {
    if (!term) return nodes;

    const lower = term.toLowerCase();

    return nodes
      .map((node) => {
        const children = filterTree(node.children || [], term);
        const match = node.name.toLowerCase().includes(lower);

        if (match || children.length) {
          return { ...node, children };
        }
        return null;
      })
      .filter(Boolean);
  };

  const expandMatched = (nodes, expandedMap = {}) => {
    nodes.forEach((node) => {
      if (node.children?.length) {
        expandedMap[node.id] = true;
        expandMatched(node.children, expandedMap);
      }
    });
    return expandedMap;
  };

  const filteredTree = useMemo(() => {
    const result = filterTree(tree, searchTerm);
    if (searchTerm) {
      setExpanded((prev) => ({ ...prev, ...expandMatched(result) }));
    }
    return result;
  }, [tree, searchTerm]);

  const handleAdd = () => {
    if (!newItemName.trim()) return;

    const selectedNode = data.find((i) => i.id === selectedId);
    const isDir = newItemType === "directory";

    const newItem = {
      id: Math.max(...data.map((i) => i.id)) + 1,
      name: newItemName.trim(),
      parentId: selectedNode
        ? selectedNode.isDirectory
          ? selectedNode.id
          : selectedNode.parentId
        : 0,
      isDirectory: isDir,
      children: [],
    };

    onChange([...data, newItem]);
    setNewItemName("");
    setShowInput(false);

    // Expand the parent directory automatically
    if (selectedNode && selectedNode.isDirectory) {
      setExpanded((prev) => ({ ...prev, [selectedNode.id]: true }));
    }
  };

  const handleDrop = (dragId, dropId) => {
    const dragged = data.find((i) => i.id === dragId);
    const dropped = data.find((i) => i.id === dropId);
    if (!dragged || !dropped || !dropped.isDirectory) return;

    if (isDescendant(dragged.id, dropped.id)) return;

    const siblings = data.filter((i) => i.parentId === dropId);
    const maxOrder = siblings.length
      ? Math.max(...siblings.map((i) => i.order))
      : -1;

    const updated = data.map((item) =>
      item.id === dragId
        ? { ...item, parentId: dropId, order: maxOrder + 1 }
        : item
    );

    onChange(updated);
  };

  const handleReorder = (dragId, dropId, parentId, position) => {
    const dragged = data.find((i) => i.id === dragId);
    // console.log(dragged, "dragged");
    if (!dragged) return;

    // Get siblings excluding dragged item
    const siblings = data
      .filter((i) => i.parentId === parentId && i.id !== dragId)
      .sort((a, b) => a.order - b.order);

    // console.log(siblings, "dragged");

    const dropIndex = siblings.findIndex((i) => i.id === dropId);
    if (dropIndex === -1) return;

    // console.log(dropIndex, "dragged");

    const insertIndex = position === "before" ? dropIndex : dropIndex + 1;

    // console.log(insertIndex, "dragged");

    const reordered = [
      ...siblings.slice(0, insertIndex),
      dragged,
      ...siblings.slice(insertIndex),
    ];

    // Reassign order cleanly
    const reorderedWithIndex = reordered.map((item, index) => ({
      ...item,
      parentId,
      order: index,
    }));

    const updated = data
      .filter((i) => i.parentId !== parentId && i.id !== dragId)
      .concat(reorderedWithIndex);

    onChange(updated);
  };

  const isDescendant = (sourceId, targetId) => {
    const findChildren = (parentId) => {
      const children = data.filter((i) => i.parentId === parentId);
      for (let child of children) {
        if (child.id === targetId) return true;
        if (findChildren(child.id)) return true;
      }
      return false;
    };
    return findChildren(sourceId);
  };

  return (
    <div className="DNDtree" style={{ padding: 10, borderRadius: 4 }}>
      <h4 style={{ margin: "5px" }}>{title}</h4>
      {searchOpen && (
        <input
          autoFocus
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: 6,
            marginBottom: 8,
            border: "1px solid #ccc",
            borderRadius: 4,
          }}
        />
      )}
      {filteredTree.map((n) => (
        <TreeNode
          key={n.id}
          node={n}
          expanded={expanded}
          toggle={toggle}
          onDrop={handleDrop}
          onReorder={handleReorder}
          selectedId={selectedId}
          setSelected={setSelectedId}
          handleSelectItem={handleSelectItem}
          onDoubleClick={onDoubleClick}
          onFocusOut={onFocusOut}
          template={template}
        />
      ))}
    </div>
  );
};

export default TreeView;
